/* @jsx h */
import { defineComponent, h } from "vue";
import { QRCodeComponent, useLocalization } from "@lemmonhq/uikit";
import type { CampaignOffer } from "./types";
import { formatMoney, useOfferText } from "./shared";

type CouponCardProps = {
  offer: CampaignOffer;
  code: string;
  expiry: string;
  headline?: string;
  showQr?: boolean;
  showExpiry?: boolean;
  accent: string;
  textColor?: string;
  radius?: number;
};

// The coupon as the customer sees it. Shared by the email block and the offer step.
export const CouponCard = defineComponent<CouponCardProps>({
  name: "StudioCouponCard",
  props: ["offer", "code", "expiry", "headline", "showQr", "showExpiry", "accent", "textColor", "radius"],
  setup(props) {
    const { t } = useLocalization();
    const offerText = useOfferText();
    return () => {
      if (props.offer.type === "none") {
        return (
          <div class="rounded-lg border border-dashed border-base-300 p-4 text-center text-xs opacity-60">
            {t("campaigns.studio_offer.no_offer_hint")}
          </div>
        );
      }
      const offer = props.offer;
      return (
        <div
          style={{
            border: `2px dashed ${props.accent}`,
            borderRadius: `${props.radius ?? 12}px`,
            color: props.textColor,
            padding: "20px",
            textAlign: "center",
          }}
        >
          {props.headline && (
            <div style={{ fontSize: "12px", letterSpacing: "0.08em", textTransform: "uppercase", opacity: 0.7 }}>
              {props.headline}
            </div>
          )}
          <div style={{ fontSize: "28px", fontWeight: 800, color: props.accent, margin: "6px 0" }}>
            {offerText(offer)}
          </div>
          {offer.min_spend ? (
            <div style={{ fontSize: "12px", opacity: 0.7 }}>
              {t("campaigns.studio_offer.min_spend_line", [formatMoney(offer.min_spend * 100)])}
            </div>
          ) : null}
          {props.showQr !== false && (
            <div style={{ width: "96px", margin: "12px auto 6px", background: "#fff", padding: "6px", borderRadius: "8px" }}>
              <QRCodeComponent key={props.code} url={props.code} />
            </div>
          )}
          <div
            style={{
              display: "inline-block",
              fontFamily: "ui-monospace, monospace",
              fontSize: "16px",
              fontWeight: 700,
              letterSpacing: "0.12em",
              padding: "6px 12px",
              borderRadius: "6px",
              background: `${props.accent}1a`,
              marginTop: "6px",
            }}
          >
            {props.code}
          </div>
          {props.showExpiry !== false && props.expiry && (
            <div style={{ fontSize: "12px", opacity: 0.6, marginTop: "8px" }}>
              {t("campaigns.studio_offer.valid_until", [props.expiry])}
            </div>
          )}
        </div>
      );
    };
  },
});
